import { FontSizes, FontWeights } from '@fluentui/react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    FluentProvider,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { DismissableContentName } from '../../../constants/app';
import {
    VSCodeConnectCommand,
    VSCodeDevBoxExtensionLink,
    VSCodeDevBoxExtensionWebHookPrefix,
    VSCodeDevBoxUriParameter,
} from '../../../constants/dev-box-vscode-extension';
import { Metric } from '../../../constants/telemetry';
import connectWithVSCode from '../../../content/images/connect_with_vscode.webp';
import { useConnectWithVSCodeDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { dismissContent } from '../../../redux/actions/application/application-action-creators';
import { trackMetric } from '../../../utilities/telemetry/channel';
import { getFormattedMessageWithLinkValues } from '../../common/formatted-link-message';
import { ConnectWithVSCodeDialogContextProperties } from './connect-with-vscode-dialog-context';

export interface ConnectWithVSCodeDialogComponentProps extends ConnectWithVSCodeDialogContextProperties {
    open?: boolean;
    onDismiss: () => void;
    onConnect: (devBoxUri: string) => void;
}

const messages = defineMessages({
    closeButtonAriaLabel: {
        id: 'ConnectWithVSCodeDialog_CloseButton_AriaLabel',
        defaultMessage: 'Close',
        description: 'Aria label for the "x" button in the top corner of the Connect with VS Code dialog',
    },
    actionButtonAriaLabel: {
        id: 'ConnectWithVSCodeDialog_ActionButton_AriaLabel',
        defaultMessage: 'Open in VS Code',
        description: 'Aria label for the action/primary action button',
    },
    tryLaterButtonAriaLabel: {
        id: 'ConnectWithVSCodeDialog_TryLaterButton_AriaLabel',
        defaultMessage: 'Try later',
        description: 'Aria label for the try later button',
    },
    dontShowAgainButtonAriaLabel: {
        id: 'ConnectWithVSCodeDialog_DontShowAgainButton_AriaLabel',
        defaultMessage: "Don't show this again",
        description: 'Aria label for the dont show again button',
    },
});

/**
 * Styles
 */
const dialogWidth = '600px';
const dialogStyle: React.CSSProperties = {
    width: dialogWidth,
};

const dialogBodyStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
};

const imageContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '240px',
};

const titleContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
};

const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: FontSizes.size20,
    fontWeight: FontWeights.semibold,
};

const connectImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
};

const messageContainerStyle: React.CSSProperties = {
    padding: '4px 0 8px 0',
    flex: '1',
};

const actionsStyle: React.CSSProperties = {
    justifyContent: 'space-between',
    paddingBottom: 0,
};

const rightActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
};

/*
 * END Styles
 */

const handleVSCodeExtensionLinkClick = () => {
    trackMetric(Metric.ConnectWithVSCodeClicked, 1);
};

const vscodeExtensionLinkValues = getFormattedMessageWithLinkValues(
    VSCodeDevBoxExtensionLink,
    handleVSCodeExtensionLinkClick
);

const ConnectWithVSCodeDialog: React.FC<ConnectWithVSCodeDialogComponentProps> = (
    props: ConnectWithVSCodeDialogComponentProps
) => {
    const { open, onDismiss, onConnect, devBoxUri } = props;

    // Redux hooks
    const dispatch = useDispatch();

    // Theming hooks
    const theme = useCurrentFluent2Theme();

    // Intl hooks
    const { formatMessage } = useIntl();

    // Event handlers
    const handleConnectButtonClick = React.useCallback(() => {
        onConnect(devBoxUri);
        onDismiss();
    }, [onConnect, devBoxUri, onDismiss]);

    const handleDontShowAgainClick = React.useCallback(() => {
        dispatch(dismissContent({ content: DismissableContentName.ConnectVSCodeDialogDismissed }));
        onDismiss();
    }, [dispatch, onDismiss]);

    return (
        <FluentProvider theme={theme}>
            <Dialog modalType="alert" open={open} onOpenChange={onDismiss}>
                <DialogSurface style={dialogStyle}>
                    <DialogBody style={dialogBodyStyle}>
                        {/* Image Section */}
                        <div style={imageContainerStyle}>
                            <img src={connectWithVSCode} alt="" style={connectImageStyle} />
                        </div>

                        <DialogContent>
                            <div style={titleContainerStyle}>
                                <DialogTitle style={titleStyle}>
                                    <FormattedMessage
                                        id="ConnectWithVSCodeDialog_Title_Text"
                                        defaultMessage="Microsoft Dev Box extension for Visual Studio Code"
                                        description="Title for the Connect with VS Code dialog"
                                    />
                                </DialogTitle>
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label={formatMessage(messages.closeButtonAriaLabel)}
                                        icon={<Dismiss24Regular />}
                                    />
                                </DialogTrigger>
                            </div>
                            <div style={messageContainerStyle}>
                                <FormattedMessage
                                    id="ConnectWithVSCodeDialog_Message_Text"
                                    defaultMessage="Connect directly to your dev box from the editor, add dev box tools to Github Copilot, and simplify dev box customizations with the <Link>Microsoft Dev Box Extension</Link>"
                                    description="Message content for the Connect with VS Code dialog. <Link> and </Link> should not be localized, but text between them should."
                                    values={vscodeExtensionLinkValues}
                                />
                            </div>
                        </DialogContent>
                        <DialogActions style={actionsStyle}>
                            <Button
                                appearance="subtle"
                                aria-label={formatMessage(messages.dontShowAgainButtonAriaLabel)}
                                onClick={handleDontShowAgainClick}
                            >
                                <FormattedMessage
                                    id="ConnectWithVSCodeDialog_DontShowAgainButton_Text"
                                    defaultMessage="Don't show this again"
                                    description="Text for the dont show again button"
                                />
                            </Button>
                            <div style={rightActionsStyle}>
                                <Button
                                    appearance="primary"
                                    aria-label={formatMessage(messages.actionButtonAriaLabel)}
                                    onClick={handleConnectButtonClick}
                                >
                                    <FormattedMessage
                                        id="ConnectWithVSCodeDialog_ActionButton_Text"
                                        defaultMessage="Open in VS Code"
                                        description="Text for the action/primary action button"
                                    />
                                </Button>
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="secondary"
                                        aria-label={formatMessage(messages.tryLaterButtonAriaLabel)}
                                    >
                                        <FormattedMessage
                                            id="ConnectWithVSCodeDialog_TryLaterButton_Text"
                                            defaultMessage="Try later"
                                            description="Text for the try later button"
                                        />
                                    </Button>
                                </DialogTrigger>
                            </div>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </FluentProvider>
    );
};

export default ConnectWithVSCodeDialog;

export const ConnectWithVSCodeDialogContextWrapper: React.FC = () => {
    // Context hooks
    const { isOpen, closeSurface, properties } = useConnectWithVSCodeDialogContext();

    // Connection handler
    const handleConnect = React.useCallback((devBoxUri: string) => {
        const vscodeExtensionUri = `${VSCodeDevBoxExtensionWebHookPrefix}/${VSCodeConnectCommand}?${VSCodeDevBoxUriParameter}=${devBoxUri}`;
        window.open(vscodeExtensionUri, '_blank');
        trackMetric(Metric.ConnectWithVSCodeClicked, 1);
    }, []);

    if (!isOpen || !properties) {
        return <></>;
    }

    return <ConnectWithVSCodeDialog {...properties} open={isOpen} onDismiss={closeSurface} onConnect={handleConnect} />;
};
